import React, { useState, useEffect } from 'react';
import { infoscreenAPI } from '../api/endpoints';
import { OFFENER_PROZESS_TEMPLATE, TemplateVariable, VariableType } from '../infoscreenTemplate';
import { buildInfoscreenDoc } from '../infoscreenPreview';
import './InfoscreenManager.css';

// Built-in tokens are resolved by the renderer, not editable as variables.
const BUILTIN_TOKENS = ['API_BASE', 'ASSET_BASE', 'INFOSCREEN_ID', 'BASE_W', 'BASE_H'];

// Collect every {{placeholder}} used across the given sources.
const collectPlaceholders = (...sources: string[]): string[] => {
  const found = new Set<string>();
  const re = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
  sources.forEach((s) => {
    let m;
    while ((m = re.exec(s || '')) !== null) {
      if (!BUILTIN_TOKENS.includes(m[1])) found.add(m[1]);
    }
  });
  return Array.from(found);
};

type TickerType = 'info' | 'warning' | 'alert' | 'ofz' | 'ofztype';

const TICKER_TYPE_OPTIONS: { value: TickerType; label: string }[] = [
  { value: 'info', label: 'ℹ️ Info' },
  { value: 'warning', label: '⚠️ Warnung' },
  { value: 'alert', label: '🚨 Alarm' },
  { value: 'ofz', label: '🗂️ ofz (Öffnungszeiten-Karte)' },
  { value: 'ofztype', label: '📋 ofztype (Karte mit Zeilen)' },
];

interface Ticker {
  _id: string;
  text: string;
  subtext: string;
  type: TickerType;
  startTime: string;
  endTime: string;
}

interface TimeSlot {
  day: number;
  open: string;
  close: string;
  closed: boolean;
}

interface OpeningHoursSection {
  _id: string;
  name: string;
  slots: TimeSlot[];
}

interface Infoscreen {
  _id: string;
  name: string;
  description: string;
  feedUrl: string;
  feedSourceUrl: string;
  feedRefreshMinutes: number;
  lastFeedDumpAt?: string;
  feedItemsMin: number;
  feedItemsMax: number;
  tickerMax: number;
  tickers: Ticker[];
  openingHours: OpeningHoursSection[];
  customCss: string;
  customHtml: string;
  customJs: string;
  variables: TemplateVariable[];
  baseWidth: number;
  baseHeight: number;
}

interface FormState {
  name: string;
  description: string;
  feedUrl: string;
  feedSourceUrl: string;
  feedRefreshMinutes: number;
  feedItemsMin: number;
  feedItemsMax: number;
  tickerMax: number;
  customCss: string;
  customHtml: string;
  customJs: string;
  variables: TemplateVariable[];
  baseWidth: number;
  baseHeight: number;
}

const emptyForm: FormState = {
  name: '',
  description: '',
  feedUrl: '',
  feedSourceUrl: '',
  feedRefreshMinutes: 15,
  feedItemsMin: 3,
  feedItemsMax: 5,
  tickerMax: 5,
  customCss: '',
  customHtml: '',
  customJs: '',
  variables: [],
  baseWidth: 1920,
  baseHeight: 1080,
};

const emptyTicker: { text: string; subtext: string; type: TickerType; startTime: string; endTime: string } = {
  text: '',
  subtext: '',
  type: 'info',
  startTime: '',
  endTime: '',
};

type Tab = 'settings' | 'variables' | 'tickers' | 'hours' | 'design' | 'preview';

const VARIABLE_TYPES: VariableType[] = ['text', 'textarea', 'url', 'image', 'color', 'number'];

// Backend API base + origin, used for the live preview and {{ASSET_BASE}} images.
// Must stay ABSOLUTE (iframe srcdoc can't resolve relative URLs), so when the
// configured API URL is relative ("/api" in production) fall back to the page origin.
const rawApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const apiOrigin = /^https?:\/\//i.test(rawApiUrl)
  ? rawApiUrl.replace(/\/api\/?$/, '')
  : window.location.origin;
const apiBase = `${apiOrigin}/api`;

const DAY_NAMES = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

const defaultSlots = (): TimeSlot[] =>
  Array.from({ length: 7 }, (_, i) => ({ day: i, open: '09:00', close: '17:00', closed: i >= 5 }));

export const InfoscreenManager: React.FC = () => {
  const [infoscreens, setInfoscreens] = useState<Infoscreen[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [activeTab, setActiveTab] = useState<Tab>('settings');
  const [tickerForm, setTickerForm] = useState({ ...emptyTicker });
  const [editTickerId, setEditTickerId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [ohName, setOhName] = useState('');
  const [ohSlots, setOhSlots] = useState<TimeSlot[]>(defaultSlots());
  const [editOhId, setEditOhId] = useState<string | null>(null);
  const [previewNonce, setPreviewNonce] = useState(0);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const res = await infoscreenAPI.getAll();
      setInfoscreens(res.data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const selected = infoscreens.find(i => i._id === selectedId) || null;

  const openCreate = () => {
    setSelectedId(null);
    setForm({ ...emptyForm });
    setShowCreate(true);
    setActiveTab('settings');
  };

  const openEdit = (is: Infoscreen) => {
    setSelectedId(is._id);
    setForm({
      name: is.name,
      description: is.description || '',
      feedUrl: is.feedUrl || '',
      feedSourceUrl: is.feedSourceUrl || '',
      feedRefreshMinutes: is.feedRefreshMinutes || 15,
      feedItemsMin: is.feedItemsMin,
      feedItemsMax: is.feedItemsMax,
      tickerMax: is.tickerMax,
      customCss: is.customCss || '',
      customHtml: is.customHtml || '',
      customJs: is.customJs || '',
      variables: is.variables || [],
      baseWidth: is.baseWidth || 1920,
      baseHeight: is.baseHeight || 1080,
    });
    setShowCreate(true);
    setActiveTab('settings');
  };

  const handleSave = async () => {
    try {
      if (selectedId) {
        await infoscreenAPI.update(selectedId, form);
      } else {
        const res = await infoscreenAPI.create(form);
        setSelectedId(res.data.data._id);
      }
      fetchAll();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Infoscreen wirklich löschen?')) return;
    try {
      await infoscreenAPI.delete(id);
      if (selectedId === id) {
        setSelectedId(null);
        setShowCreate(false);
      }
      fetchAll();
    } catch (e) {
      alert('Fehler beim Löschen');
    }
  };

  // Variables
  const updateVariable = (index: number, field: keyof TemplateVariable, value: string) => {
    setForm(f => ({
      ...f,
      variables: f.variables.map((v, i) => (i === index ? { ...v, [field]: value } : v)),
    }));
  };

  const addVariable = () => {
    setForm(f => ({
      ...f,
      variables: [...f.variables, { key: '', label: '', type: 'text' as VariableType, value: '' }],
    }));
  };

  const removeVariable = (index: number) => {
    setForm(f => ({ ...f, variables: f.variables.filter((_, i) => i !== index) }));
  };

  // Add any {{placeholder}} used in HTML/CSS/JS that has no variable yet.
  const syncDetectedVariables = () => {
    const used = collectPlaceholders(form.customHtml, form.customCss, form.customJs);
    const existing = new Set(form.variables.map(v => v.key));
    const missing = used.filter(k => !existing.has(k));
    if (missing.length === 0) {
      alert('Keine neuen Platzhalter gefunden.');
      return;
    }
    setForm(f => ({
      ...f,
      variables: [
        ...f.variables,
        ...missing.map(k => ({ key: k, label: k, type: 'text' as VariableType, value: '' })),
      ],
    }));
  };

  const loadTemplate = () => {
    if (!window.confirm('Vorlage "Offener Prozess" laden? HTML, CSS, JS und Variablen werden überschrieben.')) return;
    const t = OFFENER_PROZESS_TEMPLATE;
    setForm(f => ({
      ...f,
      name: f.name || t.name,
      baseWidth: t.baseWidth,
      baseHeight: t.baseHeight,
      customHtml: t.customHtml,
      customCss: t.customCss,
      customJs: t.customJs,
      variables: t.variables.map(v => ({ ...v })),
    }));
    setActiveTab('variables');
  };

  const handleDumpFeed = async () => {
    if (!selectedId) return;
    try {
      await infoscreenAPI.update(selectedId, form); // persist current feedSourceUrl/interval first
      const res = await infoscreenAPI.dumpFeed(selectedId);
      alert('Feed gedumpt: ' + new Date(res.data.lastFeedDumpAt).toLocaleString('de-DE'));
      fetchAll();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Feed-Dump fehlgeschlagen');
    }
  };

  // Ticker CRUD
  const handleAddTicker = async () => {
    if (!selectedId || !tickerForm.text || !tickerForm.startTime || !tickerForm.endTime) return;
    try {
      await infoscreenAPI.addTicker(selectedId, tickerForm);
      setTickerForm({ ...emptyTicker });
      fetchAll();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Fehler beim Erstellen');
    }
  };

  const handleUpdateTicker = async () => {
    if (!selectedId || !editTickerId) return;
    try {
      await infoscreenAPI.updateTicker(selectedId, editTickerId, tickerForm);
      setEditTickerId(null);
      setTickerForm({ ...emptyTicker });
      fetchAll();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Fehler beim Aktualisieren');
    }
  };

  const handleDeleteTicker = async (tickerId: string) => {
    if (!selectedId) return;
    if (!window.confirm('Ticker löschen?')) return;
    try {
      await infoscreenAPI.deleteTicker(selectedId, tickerId);
      fetchAll();
    } catch (e) {
      alert('Fehler beim Löschen');
    }
  };

  const startEditTicker = (t: Ticker) => {
    setEditTickerId(t._id);
    setTickerForm({
      text: t.text,
      subtext: t.subtext || '',
      type: t.type as TickerType,
      startTime: t.startTime ? t.startTime.slice(0, 16) : '',
      endTime: t.endTime ? t.endTime.slice(0, 16) : '',
    });
  };

  const isTickerActive = (t: Ticker) => {
    const now = new Date();
    return new Date(t.startTime) <= now && now <= new Date(t.endTime);
  };

  const formatDT = (iso: string) => {
    if (!iso) return '–';
    return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Opening Hours CRUD
  const resetOhForm = () => {
    setOhName('');
    setOhSlots(defaultSlots());
    setEditOhId(null);
  };

  const handleAddOh = async () => {
    if (!selectedId || !ohName) return;
    try {
      await infoscreenAPI.addOpeningHours(selectedId, { name: ohName, slots: ohSlots });
      resetOhForm();
      fetchAll();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Fehler beim Erstellen');
    }
  };

  const handleUpdateOh = async () => {
    if (!selectedId || !editOhId) return;
    try {
      await infoscreenAPI.updateOpeningHours(selectedId, editOhId, { name: ohName, slots: ohSlots });
      resetOhForm();
      fetchAll();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Fehler beim Aktualisieren');
    }
  };

  const handleDeleteOh = async (sectionId: string) => {
    if (!selectedId) return;
    if (!window.confirm('Bereich löschen?')) return;
    try {
      await infoscreenAPI.deleteOpeningHours(selectedId, sectionId);
      fetchAll();
    } catch (e) {
      alert('Fehler beim Löschen');
    }
  };

  const startEditOh = (s: OpeningHoursSection) => {
    setEditOhId(s._id);
    setOhName(s.name);
    const filled = defaultSlots().map(ds => {
      const existing = s.slots.find(sl => sl.day === ds.day);
      return existing || ds;
    });
    setOhSlots(filled);
  };

  const updateSlot = (day: number, field: keyof TimeSlot, value: any) => {
    setOhSlots(prev => prev.map(s => s.day === day ? { ...s, [field]: value } : s));
  };

  const isCurrentlyOpen = (s: OpeningHoursSection) => {
    const now = new Date();
    const dayIndex = (now.getDay() + 6) % 7; // JS: 0=So → 0=Mo
    const slot = s.slots.find(sl => sl.day === dayIndex);
    if (!slot || slot.closed) return false;
    const hhmm = now.getHours().toString().padStart(2, '0') + ':' + now.getMinutes().toString().padStart(2, '0');
    return hhmm >= slot.open && hhmm <= slot.close;
  };

  return (
    <div className="is-manager">
      <div className="is-sidebar">
        <div className="is-sidebar-header">
          <h2>Infoscreens</h2>
          <button className="btn-primary" onClick={openCreate}>+ Neu</button>
        </div>
        {loading ? <p>Laden...</p> :
          infoscreens.length === 0 ? <p className="empty-hint">Noch keine Infoscreens.</p> :
          <ul className="is-list">
            {infoscreens.map(is => (
              <li key={is._id} className={`is-list-item ${selectedId === is._id ? 'active' : ''}`}>
                <div className="is-list-info" onClick={() => openEdit(is)}>
                  <strong>{is.name}</strong>
                  <span>{is.tickers.length} Ticker · {is.feedUrl ? 'Feed ✓' : 'Kein Feed'}</span>
                </div>
                <button className="btn-tiny btn-danger" onClick={() => handleDelete(is._id)} title="Löschen">✕</button>
              </li>
            ))}
          </ul>
        }
      </div>

      <div className="is-content">
        {!showCreate ? (
          <div className="is-placeholder">
            <p>Wähle einen Infoscreen aus oder erstelle einen neuen.</p>
          </div>
        ) : (
          <>
            <div className="is-tabs">
              <button className={activeTab === 'settings' ? 'active' : ''} onClick={() => setActiveTab('settings')}>⚙️ Einstellungen</button>
              <button className={activeTab === 'variables' ? 'active' : ''} onClick={() => setActiveTab('variables')}>🔧 Variablen</button>
              <button className={activeTab === 'tickers' ? 'active' : ''} onClick={() => setActiveTab('tickers')}>📢 Ticker</button>
              <button className={activeTab === 'hours' ? 'active' : ''} onClick={() => setActiveTab('hours')}>🕐 Öffnungszeiten</button>
              <button className={activeTab === 'design' ? 'active' : ''} onClick={() => setActiveTab('design')}>🎨 HTML / CSS</button>
              <button className={activeTab === 'preview' ? 'active' : ''} onClick={() => { setActiveTab('preview'); setPreviewNonce(n => n + 1); }}>👁 Vorschau</button>
            </div>

            {/* ── Settings Tab ── */}
            {activeTab === 'settings' && (
              <div className="is-tab-content">
                <h3>{selectedId ? 'Infoscreen bearbeiten' : 'Neuer Infoscreen'}</h3>
                <div className="is-form-grid">
                  <label>
                    Name *
                    <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="z.B. Foyer Display" />
                  </label>
                  <label>
                    Beschreibung
                    <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional" />
                  </label>
                  <label>
                    Feed-URL (RSS/Atom, direkt)
                    <input value={form.feedUrl} onChange={e => setForm({ ...form, feedUrl: e.target.value })} placeholder="https://example.com/feed.xml" />
                  </label>
                  <label className="is-form-wide">
                    Feed-Quelle zum Dumpen (URL)
                    <input value={form.feedSourceUrl} onChange={e => setForm({ ...form, feedSourceUrl: e.target.value })} placeholder="https://offener-prozess.de/feed/?post_type=event" />
                  </label>
                  <label>
                    Dump-Intervall (Minuten)
                    <input type="number" min={1} value={form.feedRefreshMinutes} onChange={e => setForm({ ...form, feedRefreshMinutes: Number(e.target.value) })} />
                  </label>
                  <div className="is-dump-control">
                    <button type="button" className="btn-secondary" onClick={handleDumpFeed} disabled={!selectedId || !form.feedSourceUrl} title="Feed jetzt herunterladen und speichern">↻ Feed jetzt dumpen</button>
                    {selected?.lastFeedDumpAt && <span className="is-dump-hint">Zuletzt: {formatDT(selected.lastFeedDumpAt)}</span>}
                  </div>
                  <label>
                    Feeds min. Anzeige
                    <input type="number" min={0} max={20} value={form.feedItemsMin} onChange={e => setForm({ ...form, feedItemsMin: Number(e.target.value) })} />
                  </label>
                  <label>
                    Feeds max. Anzeige
                    <input type="number" min={1} max={20} value={form.feedItemsMax} onChange={e => setForm({ ...form, feedItemsMax: Number(e.target.value) })} />
                  </label>
                  <label>
                    Max. Ticker anzeigen
                    <input type="number" min={1} max={20} value={form.tickerMax} onChange={e => setForm({ ...form, tickerMax: Number(e.target.value) })} />
                  </label>
                  <label>
                    Basis-Breite (px)
                    <input type="number" value={form.baseWidth} onChange={e => setForm({ ...form, baseWidth: Number(e.target.value) })} />
                  </label>
                  <label>
                    Basis-Höhe (px)
                    <input type="number" value={form.baseHeight} onChange={e => setForm({ ...form, baseHeight: Number(e.target.value) })} />
                  </label>
                </div>
                <div className="is-form-actions">
                  <button className="btn-save" onClick={handleSave} disabled={!form.name}>{selectedId ? 'Speichern' : 'Anlegen'}</button>
                  <button className="btn-cancel" onClick={() => { setShowCreate(false); setSelectedId(null); }}>Schließen</button>
                </div>
              </div>
            )}

            {/* ── Variables Tab ── */}
            {activeTab === 'variables' && (
              <div className="is-tab-content">
                <div className="var-header">
                  <h3>Variablen ({form.variables.length})</h3>
                  <div className="var-header-actions">
                    <button className="btn-secondary" onClick={loadTemplate} title="Offener-Prozess-Vorlage laden">📄 Vorlage laden</button>
                    <button className="btn-secondary" onClick={syncDetectedVariables} title="Platzhalter aus HTML/CSS/JS erkennen">🔍 Platzhalter erkennen</button>
                    <button className="btn-secondary" onClick={addVariable}>+ Variable</button>
                  </div>
                </div>
                <p className="design-hint">
                  Jede Variable ersetzt <code>{'{{key}}'}</code> in HTML, CSS und JS. Eingebaute Tokens:
                  <code>{'{{ASSET_BASE}}'}</code> (Ordner der hochgeladenen Dateien), <code>{'{{API_BASE}}'}</code>,
                  <code>{'{{INFOSCREEN_ID}}'}</code>, <code>{'{{BASE_W}}'}</code>, <code>{'{{BASE_H}}'}</code>.
                </p>
                {form.variables.length === 0 ? (
                  <p className="empty-hint">Noch keine Variablen. Lade eine Vorlage oder füge Variablen hinzu.</p>
                ) : (
                  <div className="var-list">
                    {form.variables.map((v, i) => (
                      <div key={i} className="var-row">
                        <div className="var-row-head">
                          <input
                            className="var-key"
                            value={v.key}
                            onChange={e => updateVariable(i, 'key', e.target.value)}
                            placeholder="key"
                            title="Platzhalter-Schlüssel"
                          />
                          <input
                            className="var-label"
                            value={v.label}
                            onChange={e => updateVariable(i, 'label', e.target.value)}
                            placeholder="Beschriftung"
                          />
                          <select value={v.type} onChange={e => updateVariable(i, 'type', e.target.value)}>
                            {VARIABLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <button className="btn-icon" onClick={() => removeVariable(i)} title="Löschen">🗑️</button>
                        </div>
                        <div className="var-row-value">
                          {v.type === 'textarea' ? (
                            <textarea value={v.value} onChange={e => updateVariable(i, 'value', e.target.value)} rows={3} spellCheck={false} />
                          ) : v.type === 'color' ? (
                            <div className="var-color">
                              <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(v.value) ? v.value : '#000000'} onChange={e => updateVariable(i, 'value', e.target.value)} />
                              <input value={v.value} onChange={e => updateVariable(i, 'value', e.target.value)} placeholder="#1e2758" />
                            </div>
                          ) : v.type === 'number' ? (
                            <input type="number" value={v.value} onChange={e => updateVariable(i, 'value', e.target.value)} />
                          ) : v.type === 'image' ? (
                            <div className="var-image">
                              <input value={v.value} onChange={e => updateVariable(i, 'value', e.target.value)} placeholder="{{ASSET_BASE}}/logo.svg" />
                              {v.value && <img className="var-preview" src={v.value.replace('{{ASSET_BASE}}', `${apiOrigin}/uploads/infoscreen`)} alt="" />}
                            </div>
                          ) : (
                            <input value={v.value} onChange={e => updateVariable(i, 'value', e.target.value)} placeholder="Wert" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="is-form-actions">
                  <button className="btn-save" onClick={handleSave} disabled={!form.name}>Speichern</button>
                </div>
              </div>
            )}

            {/* ── Tickers Tab ── */}
            {activeTab === 'tickers' && (
              <div className="is-tab-content">
                <h3>Ticker-Meldungen {selectedId && selected ? `(${selected.tickers.length})` : ''}</h3>
                {!selectedId ? (
                  <p className="empty-hint">Speichere den Infoscreen zuerst, um Ticker hinzuzufügen.</p>
                ) : (
                  <>
                    <div className="ticker-form">
                      <div className="ticker-form-row">
                        <label>
                          Text *
                          <input value={tickerForm.text} onChange={e => setTickerForm({ ...tickerForm, text: e.target.value })} placeholder="Ticker-Nachricht" />
                        </label>
                        <label>
                          Subtext
                          <input value={tickerForm.subtext} onChange={e => setTickerForm({ ...tickerForm, subtext: e.target.value })} placeholder="Zusatzinfo" />
                        </label>
                      </div>
                      <div className="ticker-form-row">
                        <label>
                          Typ
                          <select value={tickerForm.type} onChange={e => setTickerForm({ ...tickerForm, type: e.target.value as TickerType })}>
                            {TICKER_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </label>
                        <label>
                          Start
                          <input type="datetime-local" value={tickerForm.startTime} onChange={e => setTickerForm({ ...tickerForm, startTime: e.target.value })} />
                        </label>
                        <label>
                          Ende
                          <input type="datetime-local" value={tickerForm.endTime} onChange={e => setTickerForm({ ...tickerForm, endTime: e.target.value })} />
                        </label>
                      </div>
                      <div className="ticker-form-actions">
                        {editTickerId ? (
                          <>
                            <button className="btn-save" onClick={handleUpdateTicker}>Aktualisieren</button>
                            <button className="btn-cancel" onClick={() => { setEditTickerId(null); setTickerForm({ ...emptyTicker }); }}>Abbrechen</button>
                          </>
                        ) : (
                          <button className="btn-save" onClick={handleAddTicker} disabled={!tickerForm.text || !tickerForm.startTime || !tickerForm.endTime}>Ticker hinzufügen</button>
                        )}
                      </div>
                    </div>

                    {selected && selected.tickers.length > 0 && (
                      <div className="ticker-list">
                        {selected.tickers.map(t => (
                          <div key={t._id} className={`ticker-item ticker-${t.type} ${isTickerActive(t) ? 'ticker-active' : 'ticker-inactive'}`}>
                            <div className="ticker-item-content">
                              <div className="ticker-item-text">{t.text}</div>
                              {t.subtext && <div className="ticker-item-sub">{t.subtext}</div>}
                              <div className="ticker-item-meta">
                                <span className={`ticker-type-badge ${t.type}`}>{t.type}</span>
                                <span>{formatDT(t.startTime)} – {formatDT(t.endTime)}</span>
                                {isTickerActive(t) ? <span className="ticker-status active">Aktiv</span> : <span className="ticker-status">Inaktiv</span>}
                              </div>
                            </div>
                            <div className="ticker-item-actions">
                              <button className="btn-icon" onClick={() => startEditTicker(t)} title="Bearbeiten">✏️</button>
                              <button className="btn-icon" onClick={() => handleDeleteTicker(t._id)} title="Löschen">🗑️</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ── Opening Hours Tab ── */}
            {activeTab === 'hours' && (
              <div className="is-tab-content">
                <h3>Öffnungszeiten {selectedId && selected ? `(${(selected.openingHours || []).length} Bereiche)` : ''}</h3>
                {!selectedId ? (
                  <p className="empty-hint">Speichere den Infoscreen zuerst, um Öffnungszeiten hinzuzufügen.</p>
                ) : (
                  <>
                    <div className="oh-form">
                      <div className="oh-form-header">
                        <label>
                          Bereichsname *
                          <input value={ohName} onChange={e => setOhName(e.target.value)} placeholder="z.B. Ausstellung, Archiv, Café" />
                        </label>
                      </div>
                      <div className="oh-slots">
                        {ohSlots.map(slot => (
                          <div key={slot.day} className={`oh-slot-row ${slot.closed ? 'oh-closed' : ''}`}>
                            <span className="oh-day">{DAY_NAMES[slot.day]}</span>
                            <label className="oh-closed-toggle">
                              <input type="checkbox" checked={slot.closed} onChange={e => updateSlot(slot.day, 'closed', e.target.checked)} />
                              Geschlossen
                            </label>
                            {!slot.closed && (
                              <>
                                <input type="time" value={slot.open} onChange={e => updateSlot(slot.day, 'open', e.target.value)} />
                                <span className="oh-dash">–</span>
                                <input type="time" value={slot.close} onChange={e => updateSlot(slot.day, 'close', e.target.value)} />
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="oh-form-actions">
                        {editOhId ? (
                          <>
                            <button className="btn-save" onClick={handleUpdateOh}>Aktualisieren</button>
                            <button className="btn-cancel" onClick={resetOhForm}>Abbrechen</button>
                          </>
                        ) : (
                          <button className="btn-save" onClick={handleAddOh} disabled={!ohName}>Bereich hinzufügen</button>
                        )}
                      </div>
                    </div>

                    {selected && (selected.openingHours || []).length > 0 && (
                      <div className="oh-list">
                        {selected.openingHours.map(s => (
                          <div key={s._id} className="oh-section-card">
                            <div className="oh-section-header">
                              <strong>{s.name}</strong>
                              <span className={`oh-status-badge ${isCurrentlyOpen(s) ? 'open' : 'closed'}`}>
                                {isCurrentlyOpen(s) ? 'Geöffnet' : 'Geschlossen'}
                              </span>
                              <div className="oh-section-actions">
                                <button className="btn-icon" onClick={() => startEditOh(s)} title="Bearbeiten">✏️</button>
                                <button className="btn-icon" onClick={() => handleDeleteOh(s._id)} title="Löschen">🗑️</button>
                              </div>
                            </div>
                            <div className="oh-section-slots">
                              {(s.slots || []).sort((a, b) => a.day - b.day).map(sl => (
                                <div key={sl.day} className={`oh-display-row ${sl.closed ? 'oh-display-closed' : ''}`}>
                                  <span>{DAY_NAMES[sl.day]}</span>
                                  <span>{sl.closed ? 'Geschlossen' : `${sl.open} – ${sl.close}`}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ── Design Tab ── */}
            {activeTab === 'design' && (
              <div className="is-tab-content">
                <div className="var-header">
                  <h3>HTML, CSS &amp; JS anpassen</h3>
                  <button className="btn-secondary" onClick={loadTemplate} title="Offener-Prozess-Vorlage laden">📄 Vorlage laden</button>
                </div>
                <p className="design-hint">
                  Der <code>&lt;body&gt;</code>-Inhalt wird hier definiert, CSS als <code>&lt;style&gt;</code>-Block eingefügt.
                  Platzhalter <code>{'{{key}}'}</code> werden aus den <strong>Variablen</strong> ersetzt. Ist ein eigenes
                  JavaScript gesetzt, ersetzt es das eingebaute Render-Script (Ticker, Feed, Skalierung).
                </p>
                <div className="design-editors">
                  <div className="editor-block">
                    <label>HTML (Body-Inhalt)</label>
                    <textarea
                      className="code-editor"
                      value={form.customHtml}
                      onChange={e => setForm({ ...form, customHtml: e.target.value })}
                      placeholder={'<div id="scale-wrapper">\n  <!-- Dein Layout hier -->\n  <div id="live-ticker"></div>\n  <div id="feed-list"></div>\n</div>'}
                      rows={18}
                      spellCheck={false}
                    />
                  </div>
                  <div className="editor-block">
                    <label>CSS</label>
                    <textarea
                      className="code-editor css-editor"
                      value={form.customCss}
                      onChange={e => setForm({ ...form, customCss: e.target.value })}
                      placeholder={"#scale-wrapper {\n  width: {{BASE_W}}px;\n  height: {{BASE_H}}px;\n  background: {{pageBackground}};\n}"}
                      rows={18}
                      spellCheck={false}
                    />
                  </div>
                  <div className="editor-block editor-block--full">
                    <label>JavaScript (optional – ersetzt das Standard-Script)</label>
                    <textarea
                      className="code-editor"
                      value={form.customJs}
                      onChange={e => setForm({ ...form, customJs: e.target.value })}
                      placeholder={"// Eigenes Render-Script. Leer lassen für das eingebaute Ticker/Feed-Script."}
                      rows={14}
                      spellCheck={false}
                    />
                  </div>
                </div>
                <div className="is-form-actions">
                  <button className="btn-save" onClick={handleSave} disabled={!form.name}>Speichern</button>
                </div>
              </div>
            )}

            {/* ── Preview Tab ── */}
            {activeTab === 'preview' && (() => {
              const previewConfig = {
                _id: selectedId || 'preview',
                name: form.name,
                feedUrl: form.feedUrl,
                feedItemsMin: form.feedItemsMin,
                feedItemsMax: form.feedItemsMax,
                tickerMax: form.tickerMax,
                customCss: form.customCss,
                customHtml: form.customHtml,
                customJs: form.customJs,
                variables: form.variables,
                openingHours: selected?.openingHours || [],
              };
              const doc = buildInfoscreenDoc(previewConfig, apiBase, form.baseWidth || 1920, form.baseHeight || 1080);
              const aspect = (form.baseWidth || 1920) / (form.baseHeight || 1080);
              return (
                <div className="is-tab-content">
                  <div className="var-header">
                    <h3>Vorschau</h3>
                    <button className="btn-secondary" onClick={() => setPreviewNonce(n => n + 1)} title="Vorschau neu laden">↻ Aktualisieren</button>
                  </div>
                  <p className="design-hint">
                    Live-Vorschau des aktuellen Standes (HTML/CSS/JS + Variablen). Feed, Ticker und Öffnungszeiten
                    werden aus dem CMS geladen – {selectedId ? 'gespeicherte Änderungen daran werden angezeigt.' : 'dafür muss der Infoscreen zuerst gespeichert werden.'}
                  </p>
                  <div className="is-preview-stage">
                    <div className="is-preview-frame" style={{ aspectRatio: String(aspect) }}>
                      <iframe
                        key={previewNonce}
                        title="Infoscreen-Vorschau"
                        srcDoc={doc}
                        sandbox="allow-scripts allow-same-origin"
                        className="is-preview-iframe"
                      />
                    </div>
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
};
